import currency from "currency.js";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { SideResponse } from "../../codegen-api";
import { COLORS, TEXT_COLORS } from "../../constants/design/colors";
import {
  formatKnownValue,
  formatRoiPercent,
  signedTone,
} from "../../utils/unknownNumbers";
import { MarketInstrumentContext } from "../../contexts/MarketInstrumentContext";
import {
  formatCurrencyWithSubscript,
  formatSizePrecision,
} from "../../utils/format";
import { SPACING } from "../../constants/design/spacing";
import {
  ViewDetailsContent,
  ViewDetailsRow,
} from "../PortfolioSettings/Table/style";
import { FooterContainer } from "./style";

interface IAdjustIsolatedMarginModalDetailsProps {
  position: any;
  profitPercent: number | undefined;
  accountLeverage: number | string | undefined;
  /** `undefined` when unknown (null equity / margin from the backend). */
  marginBalance: number | undefined;
  marginError: string | undefined;
  isAddMargin: boolean;
}

function AdjustIsolatedMarginModalDetails({
  position,
  profitPercent,
  accountLeverage,
  marginBalance,
  marginError,
  isAddMargin,
}: IAdjustIsolatedMarginModalDetailsProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "shared.ViewDetailsModal.AdjustIsolatedMarginModal",
  });
  const { getMarketPrecision } = useContext(MarketInstrumentContext);

  return (
    <ViewDetailsContent style={{ marginTop: SPACING.three }}>
      <ViewDetailsRow>
        <span>{t("unrealized_pnl")}</span>
        <span
          style={{
            color: signedTone(position.unrealized_pnl, {
              up: COLORS.up.one,
              down: COLORS.down.one,
              unknown: TEXT_COLORS.three,
            }),
          }}
        >
          {formatCurrencyWithSubscript(position.unrealized_pnl, {
            showPositiveSign: true,
          })}
        </span>
      </ViewDetailsRow>
      <ViewDetailsRow>
        <span>{t("roi")}</span>
        <span
          style={{
            color: signedTone(profitPercent, {
              up: COLORS.up.one,
              down: COLORS.down.one,
              unknown: TEXT_COLORS.three,
            }),
          }}
        >
          {formatRoiPercent(profitPercent)}
        </span>
      </ViewDetailsRow>
      <ViewDetailsRow>
        <span>{t("contracts")}</span>
        <span>{formatSizePrecision(position.amount)}</span>
      </ViewDetailsRow>
      <ViewDetailsRow>
        <span>{t("side")}</span>
        <span
          style={{
            color:
              position.side === SideResponse.Buy
                ? COLORS.up.one
                : COLORS.down.one,
          }}
        >
          {position.side === SideResponse.Buy ? t("long") : t("short")}
        </span>
      </ViewDetailsRow>
      <ViewDetailsRow>
        <span>{t("avg_entry_price")}</span>
        <span>
          {currency(position.avg_entry_price || 0, {
            precision: getMarketPrecision(
              position.asset,
              position.instrument_type
            ).price_precision,
          }).format()}
        </span>
      </ViewDetailsRow>
      <ViewDetailsRow>
        <span>{t("isolated_margin")}</span>
        <span>{currency(Number(position.isolated_margin)).format()}</span>
      </ViewDetailsRow>
      {!position.option ? (
        <ViewDetailsRow
          style={{
            textTransform: "none",
          }}
        >
          <span>{t("leverage")}</span>
          <span>{accountLeverage}x</span>
        </ViewDetailsRow>
      ) : null}
      {position.liquidation_price ? (
        <ViewDetailsRow>
          <span>{t("liquidation_price")}</span>
          <span>
            {currency(position.liquidation_price, {
              precision: getMarketPrecision(
                position.asset,
                position.instrument_type
              ).price_precision,
            }).format()}
          </span>
        </ViewDetailsRow>
      ) : null}
      <FooterContainer>
        <ViewDetailsRow>
          <span>{t("margin_balance")}</span>
          <span
            style={
              marginError
                ? { color: isAddMargin ? COLORS.negative.one : undefined }
                : undefined
            }
          >
            {formatKnownValue(marginBalance)}
          </span>
        </ViewDetailsRow>
      </FooterContainer>
    </ViewDetailsContent>
  );
}

export default AdjustIsolatedMarginModalDetails;
